import { useSetupNeed } from "@/hooks/use-setup-need";
import {
  SkyBadge,
  SkyButton,
  SkyCard,
  SkyDataGrid,
  SkyDialog,
  SkyDropdown,
  SkyInput,
  SkyMenu,
  SkyMessageBar,
  SkySkeleton,
  SkySkeletonItem,
  SkyText,
  SkyTooltip,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { Link, useLocation } from "wouter";
import type { DataflowSummary } from "@shared/types/dataflows";
import type { DestinationSummaryView } from "@shared/types/instances";
import { WATCH_DEFAULT_OUTCOMES, WATCH_NO_EMAIL_MESSAGE } from "@shared/types/notifications";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import {
  CancelImportDialog,
  NOT_WHILE_IMPORTING,
  type CancelImportTarget,
} from "@/components/connections/CancelImportDialog";
import { connectionRunColumns } from "@/components/connections/ConnectionRunColumns";
import {
  ConnectionStatusCell,
  ConnectionsImportLiveLine,
  LastRunCell,
} from "@/components/connections/ConnectionStateBadge";
import {
  CONNECTION_STATUSES,
  connectionStatusOf,
  connectionStatusPresentation,
  isListedRunning,
  type ConnectionStatus,
} from "@/components/connections/ConnectionStatus";
import { ConnectorLogo } from "@/components/connections/ConnectorLogo";
import { useConnectionToggle } from "@/components/connections/DisableConnectionDialog";
import { importOffReason } from "@/components/connections/LastRunPanel";
import { ImportSelectedResult } from "@/components/connections/ImportSelectedResult";
import { WATCH_UNAVAILABLE_MESSAGE, useMyWatches, useSetWatch } from "@/hooks/use-alert-routing";
import { useDestinationSummary } from "@/hooks/use-binding";
import { useConnectors } from "@/hooks/use-connectors";
import { CredentialListBadge } from "@/components/credentials/CredentialStatus";
import { credentialTarget, useCredentials } from "@/hooks/use-credentials";
import { useDataflows, useDeleteDataflow } from "@/hooks/use-dataflows";
import { useLiveRunRefresh } from "@/hooks/use-live-run-refresh";
import { isTerminalRun } from "@/components/runs/runFormatting";
import { useSchedules } from "@/hooks/use-schedules";
import {
  IMPORT_SELECTED_MAX,
  useImportSelected,
  type NotStartedRow,
} from "@/hooks/use-import-selected";
import {
  importStartNoticeKey,
  newestRunId,
  startedAheadOfList,
  useImportStartRefusals,
  useStartImport,
} from "@/hooks/use-run-activity";
import { pixelColumnLayout } from "@/lib/grid";
import { usePinnedColumnEdge } from "@/lib/gridPinned";
import { stopRowSelection } from "@/lib/gridRow";
import { WatchBadge } from "@/components/notifications/WatchBadge";
import { useStartedRuns } from "@/lib/live/InstanceRunFeed";
import { usePermissions } from "@/hooks/use-permissions";
import { failureMessage, type ApiClientError } from "@/lib/api";
import {
  AddIcon,
  ArrowRightIcon,
  CancelIcon,
  DeleteIcon,
  EditIcon,
  PauseIcon,
  PlayIcon,
  RetryIcon,
  SearchIcon,
  ViewIcon,
  WatchIcon,
  WatchingIcon,
} from "@/lib/icons";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import { NEW_CONNECTION_PATH, absolutePath, connectionPath } from "@/lib/routes";
import { RunNowDialog } from "@/components/runs/RunNowDialog";
import {
  LastLoadedLine,
  LateBadge,
  connectionReliabilityOf,
} from "@/components/reliability/Freshness";
import { useReliability } from "@/hooks/use-reliability";
import { destinationBlockReason, useDestinationReadiness } from "@/hooks/use-destination-readiness";

/**
 * The connections list (UX-3, FR-13, NFR-8).
 *
 * What a data engineer opens first: what this instance imports from, whether
 * each connection is ready, and what ran last. The outcome and the entity count
 * sit in the row so triage needs no clicks.
 *
 * Search, connector and status filter the loaded list in the browser — the WHOLE
 * loaded list, not the visible page, because the filters run before the grid is
 * handed its data. The scale target is 50 connections per instance (NFR-5), so a
 * server-side filter would cost a round trip to narrow a list that is already in
 * memory; the result count goes to a polite live region so a screen-reader user
 * hears the list change (NFR-7).
 *
 * Search covers the connection's name and display name, its description, and the
 * connector by BOTH its display name and its slug, so what is on the screen and
 * what is in the seed file both find the row.
 *
 * Hiding a control is a courtesy, not the control: the server refuses the same
 * action for the same permission (FR-13). A Viewer therefore sees the names and
 * the statuses, and no way to create, run or delete.
 *
 * Status (owner feedback, 2026-09-28) is the connection's one word, derived in the browser from
 * its setup state and its runs (`ConnectionStatus.tsx`): Setup incomplete, Ready, Running,
 * Failed, Active, Disabled. It replaced the State column; the filter keeps its test id.
 */

const ALL = "__all__";

/** One empty selection, so the controlled grid is not handed a new array on every render. */
const NO_SELECTION: string[] = [];

/** The bar above the grid: the rows that did not start, and how many were past the cap. */
interface ImportResult {
  notStarted: NotStartedRow[];
  overLimit: number;
}
const NO_IMPORT_RESULT: ImportResult = { notStarted: [], overLimit: 0 };

/**
 * A list row with its status, so the grid's own client sort (it compares `row[key]`) can sort the
 * Status column by the word on screen.
 */
type ConnectionRow = DataflowSummary & { status: string };

/** The grid's own client-side sort, mirrored so "the grid's order" is the order on screen. */
interface GridSort {
  key: string;
  direction: "asc" | "desc";
}

/**
 * The rows in the order `SkyDataGrid` shows them: its client sort compares `row[key]` as text
 * (`localeCompare`), puts a missing value last when ascending, and reverses for descending
 * (skypoint-ui `useDataGridState`). `Array.prototype.sort` is stable there and here.
 */
function inGridOrder(rows: readonly ConnectionRow[], sort: GridSort | null): ConnectionRow[] {
  if (!sort) return [...rows];
  const valueOf = (row: ConnectionRow): unknown =>
    (row as unknown as Record<string, unknown>)[sort.key];
  return [...rows].sort((a, b) => {
    const left = valueOf(a);
    const right = valueOf(b);
    if (left == null && right == null) return 0;
    if (left == null) return sort.direction === "asc" ? 1 : -1;
    if (right == null) return sort.direction === "asc" ? -1 : 1;
    const order = String(left).localeCompare(String(right));
    return sort.direction === "desc" ? -order : order;
  });
}

/**
 * Rows per page. Local because the grid's own page state is what the search has
 * to reason about (see `gridKey` below), so the two cannot drift apart.
 */
const PAGE_SIZE = 25;

/** How long the typist gets between keystrokes before the list is re-filtered. */
const SEARCH_DEBOUNCE_MS = 200;

/**
 * The table's layout (round 10 wave 2, dev walk of e3da2d5): Source, Destination and Status
 * have pixel widths their widest value fits in (`connectionsList.layout.test.tsx`), so they read
 * in full at any pane; the other columns share what is left in proportion to these minimums
 * (`pixelColumnLayout`), and the floor is the sum. Wave 1's Destination column had been added at
 * 10% on top of columns already summing to 100%, so `table-fixed` shrank every one and clipped
 * "Destination" and "Snowflake" into Status at 1280 px.
 *
 * Wave 3 (dev, 2026-10-08): at 1440 px the floor (then 1216 px) was wider than the 1131 px pane,
 * so the pinned Actions column covered "Run time", the column under it. Source, Destination and
 * Status are now their measured needs, "Run time" may take two lines (header cells wrap at a
 * space here), and the floor (1128 px with the selection column) fitted the pane at 1440 then
 * (superseded by #972 below: it scrolls a little there now). At 1280 (971 px) and on a tablet the grid still
 * scrolls in its region, Actions stays pinned at the right edge (B-52), and Name, Source,
 * Destination and Status are in view before any scroll.
 *
 * Wave 3 (2026-10-09, the 607e63a guard): a header that fits is not a value that fits. Rows needs
 * 93 px for "12,345,678" (69 px at 12 px) inside its padding, and Run time 78 for "48h 00m" (54)
 * on one line (`connectionsList.status.test.tsx`); at 84 and 74 the count ran out of its cell
 * into the duration. The 13 px came from Source, Destination, Status and Actions trimmed to their
 * measured needs (161, 137, 144, 99) and Name (115 to 109, its header needs 106), so the floor
 * stayed 1128 px and fitted the pane at 1440 (until #972).
 *
 * #972 (2026-10-14): Rows is two labelled lines, "Extracted: 12,345,678" (136 px) being the widest,
 * so it needs 160 (was 93) and the floor grows by the difference, to about 1200 px. That REVERSES
 * the #432 answer below for 1440: the grid now scrolls a little there (Actions stays pinned). The
 * widths are estimates, not yet measured in Chromium, and the owner has not yet answered whether
 * the scroll is accepted (open decision on PR #1021).
 *
 * #432 (owner answer P1 = A, 2026-10-13, as it stood before #972): the Name floor stays 109. It
 * said the floor stayed 1128 so every column is in view at 1440 and Name to Last import at 1280. The "Watching: failures and warnings" button
 * (wider than the 152 px the shorter words took on one line, icon in) used to spill out of the cell, so in this grid it is the compact
 * badge: no icon, its words wrap under the name (83 px wide, measured in Chromium for the
 * shorter "Watching: failures", not re-measured for the longer words, which wrap to more lines inside the
 * 85 px the cell holds). Last import keeps 136: the widest pill there is "Partly failed" (#817),
 * not the retired "Succeeded with warning".
 */
const CONNECTIONS_MIN_COLUMN_WIDTH: Readonly<Record<string, number>> = {
  displayName: 109,
  selectedEntityCount: 123,
  lastRefreshAt: 136,
  lastRunRows: 160,
  lastRunDuration: 78,
};
/** The library's header cells never wrap; here a two-word header may ("Run time"). */
const CONNECTIONS_GRID_FLOOR =
  "[&_th]:whitespace-normal [&_table]:min-w-(--connections-grid-floor)";
/** With an Actions column the grid pins it at the right edge (B-52), as the Runs grid does. */
const CONNECTIONS_GRID_PINNED = `grid-pinned-last ${CONNECTIONS_GRID_FLOOR}`;

/**
 * The typed value, settled.
 *
 * The input itself stays uncontrolled-feeling — every keystroke paints
 * immediately — while the FILTER runs on the settled value. At the 50-connection
 * scale target (NFR-5) the filter itself is cheap; what the debounce buys is the
 * live region, which would otherwise announce a new count on every keystroke and
 * read a screen-reader user their way through "8 of 50", "3 of 50", "1 of 50"
 * while they are still typing a word.
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

function LoadingRows() {
  return (
    <SkySkeleton testId="connections-loading">
      <div className="flex flex-col gap-3 p-2">
        {[0, 1, 2, 3].map((row) => (
          <div key={row} className="flex items-center gap-4">
            <SkySkeletonItem width={220} />
            <SkySkeletonItem width={120} />
            <SkySkeletonItem width={72} borderRadius={999} />
            <SkySkeletonItem width={90} />
          </div>
        ))}
      </div>
    </SkySkeleton>
  );
}

/**
 * True while the connection's last import is queued or running (Session 10, P12). The runner's
 * `is_processing` lock covers only extract onward; the queued window is the run's own status.
 *
 * #2094 review: "queued or running" is `isListedRunning`, the Status cell's own rule, so the row
 * never reads Succeeded beside Cancel run, and the lock flag decides only for an answer with no
 * `activeRunId` (the server's own start refusal reads the run rows too, `assertRunConnectionsFree`).
 *
 * Review r1: also while this tab's start request for the row is in flight, and while this tab
 * started an import the list has not caught up with yet. The key is minted per press, so without
 * these a second press in that window would send a second start. Since Session 11 (P1, ruling D1)
 * the server refuses that second start too (409 RUN_IN_PROGRESS); this keeps the press from being
 * offered at all, and `useStartImport` drops one that still arrives (#1650).
 */
function isImporting(
  row: DataflowSummary,
  pendingDataflowId: string | null,
  started: ReadonlyMap<string, string>,
): boolean {
  return isListedRunning(row) || row.id === pendingDataflowId || startedAheadOfList(row, started);
}

/**
 * The run Cancel run stops (#977), or null when there is none to stop. Resolved as the editor
 * does: the newest of the run this tab started and the list's own, because between the start
 * answer and the list's next read the row still names the PREVIOUS, finished run. While only the
 * start request is in flight there is no run id yet, so nothing is offered.
 */
function cancellableRunId(
  row: DataflowSummary,
  started: ReadonlyMap<string, string>,
): string | null {
  const live = isListedRunning(row) || startedAheadOfList(row, started);
  if (!live) return null;
  return newestRunId(started.get(row.id), row.lastRun?.runId, row.lastRunId);
}

/** Disable or Enable for the row's menu, by whether the connection is disabled now. */
function toggleMenuItem(
  row: DataflowSummary,
  openToggle: ReturnType<typeof useConnectionToggle>["open"],
  importBusy: boolean,
) {
  if (row.state === "disabled") {
    return {
      key: "enable",
      label: "Enable connection",
      icon: <PlayIcon className="h-4 w-4" />,
      onClick: () =>
        openToggle({
          id: row.id,
          name: row.displayName,
          mode: "enable" as const,
          disabled: row.disabled ?? null,
        }),
    };
  }
  return {
    key: "disable",
    label: "Disable connection",
    icon: <PauseIcon className="h-4 w-4" />,
    // #975: not while the connection's import is queued or running.
    ...(importBusy ? { disabled: true, description: NOT_WHILE_IMPORTING } : {}),
    onClick: () =>
      openToggle({
        id: row.id,
        name: row.displayName,
        mode: "disable" as const,
      }),
  };
}

/**
 * A row's actions: View history beside the menu, then Import now / with options, open, Disable or
 * Enable, Watch or Stop watching, and Delete, each only for the role that may.
 */
function ConnectionRowActions({
  row,
  canReadRuns,
  canStartRun,
  canCancelRun,
  canWrite,
  canDelete,
  importBusy,
  cancelRunId,
  watched,
  watchUnavailable,
  watchUnavailableReason,
  navigate,
  startImport,
  rememberRefusal,
  notice,
  showApiError,
  openToggle,
  watchMutate,
  onImportWithOptions,
  onDelete,
  onCancelImport,
}: {
  row: ConnectionRow;
  canReadRuns: boolean;
  canStartRun: boolean;
  canCancelRun: boolean;
  canWrite: boolean;
  canDelete: boolean;
  /** True while the row's import is queued, running or being started from this tab. */
  importBusy: boolean;
  /** The run Cancel run stops, null while there is none (#977). */
  cancelRunId: string | null;
  watched: boolean;
  watchUnavailable: boolean;
  watchUnavailableReason: string | null;
  navigate: ReturnType<typeof useLocation>[1];
  startImport: ReturnType<typeof useStartImport>["startImport"];
  /** #1650: records the row's newest run when a refused start is posted. */
  rememberRefusal: (id: string, runId: string | null) => void;
  notice: ReturnType<typeof useNotice>;
  showApiError: ReturnType<typeof useApiErrorNotice>;
  openToggle: ReturnType<typeof useConnectionToggle>["open"];
  watchMutate: ReturnType<typeof useSetWatch>["mutate"];
  onImportWithOptions: (row: DataflowSummary) => void;
  onDelete: (row: DataflowSummary) => void;
  onCancelImport: (row: DataflowSummary, runId: string) => void;
}) {
  // #375, #923: Import now is refused by the server while the destination is not Ready.
  const destinationReason = destinationBlockReason(useDestinationReadiness());
  // #1080, #1081: why Import now and Import with options are off, said in both items.
  const setupNeed = useSetupNeed(row.connectorName, row.selectedEntityCount);
  const importOff = importOffReason(row.state, importBusy, destinationReason, setupNeed);
  return (
    // 2026-09-29: a menu item's click must not reach the row and tick its checkbox.
    <div className="flex items-center justify-end gap-1" onClick={stopRowSelection}>
      {/* Session 11 (P1, D9): the connection's Runs view, one click from the row. An
          icon-only button, so it takes hover text through the same SkyTooltip as Import
          selected; the accessible name stays the row's (S11 PR review SF2). */}
      {canReadRuns ? (
        <SkyTooltip content="View history">
          <SkyButton
            appearance="subtle"
            size="small"
            icon={ViewIcon}
            testId={`connection-history-${row.displayName}`}
            aria-label={`View history for ${row.displayName}`}
            onClick={() => navigate(absolutePath(`${connectionPath(row.id)}?tab=runs`))}
          />
        </SkyTooltip>
      ) : null}
      <SkyMenu
        align="end"
        trigger={
          <SkyButton
            appearance="subtle"
            size="small"
            testId={`connection-actions-${row.displayName}`}
            aria-label={`More actions for ${row.displayName}`}
          >
            ⋯
          </SkyButton>
        }
        items={[
          ...(canReadRuns
            ? [
                {
                  key: "history",
                  label: "View history",
                  icon: <ViewIcon className="h-4 w-4" />,
                  onClick: () => navigate(absolutePath(`${connectionPath(row.id)}?tab=runs`)),
                },
              ]
            : []),
          ...(canStartRun
            ? [
                {
                  key: "run",
                  label: "Import now",
                  icon: <PlayIcon className="h-4 w-4" />,
                  disabled: importOff !== null,
                  // A menu item has no tooltip: it says why in the item (#1081).
                  ...(importOff ? { description: importOff } : {}),
                  onClick: () => {
                    startImport(row.id, {
                      // The row's own Last import cell shows the progress; the notice says
                      // it started and opens it (ux-s10 §2, §3).
                      onSuccess: (result) =>
                        notice.success(`Import started for ${row.displayName}.`, {
                          action: {
                            label: "View import",
                            onClick: () => navigate(absolutePath(`/runs/${result.runId}`)),
                          },
                        }),
                      onError: (error) => {
                        rememberRefusal(row.id, row.lastRun?.runId ?? null);
                        showApiError(error, "We could not start the import.", {
                          key: importStartNoticeKey(row.id),
                        });
                      },
                    });
                  },
                },
                {
                  // Round 6, S5: Run now's Full refresh and Choose tables, for this row.
                  key: "run-options",
                  label: "Import with options",
                  icon: <PlayIcon className="h-4 w-4" />,
                  // #1080, #1081: off for Import now's reasons, the destination's among them.
                  disabled: importOff !== null,
                  ...(importOff ? { description: importOff } : {}),
                  onClick: () => onImportWithOptions(row),
                },
              ]
            : []),
          // #977: stop a queued or running import (the run is known once it has started).
          // The cancel route takes `runs.cancel`, not `runs.start`.
          ...(canCancelRun && importBusy && cancelRunId
            ? [
                {
                  key: "cancel-run",
                  label: "Cancel run",
                  icon: <CancelIcon className="h-4 w-4" />,
                  onClick: () => onCancelImport(row, cancelRunId),
                },
              ]
            : []),
          {
            key: "open",
            label: canWrite ? "Edit connection" : "View connection",
            // D9: the eye is View history's now; reading the connection takes the arrow.
            icon: canWrite ? (
              <EditIcon className="h-4 w-4" />
            ) : (
              <ArrowRightIcon className="h-4 w-4" />
            ),
            // #975: a reader still opens it; a writer cannot edit under a running import.
            ...(canWrite && importBusy ? { disabled: true, description: NOT_WHILE_IMPORTING } : {}),
            onClick: () => navigate(absolutePath(connectionPath(row.id))),
          },
          // 2026-09-28: pause and resume the connection (`dataflows.write`).
          ...(canWrite ? [toggleMenuItem(row, openToggle, importBusy)] : []),
          // 2026-09-28 (alert routing): the caller's own Watch, for every role that reads
          // connections. Beside Disable and Enable; the request names no user.
          watched
            ? {
                key: "unwatch",
                label: "Stop watching",
                icon: <WatchingIcon className="h-4 w-4" />,
                onClick: () =>
                  watchMutate(
                    { targetType: "dataflow" as const, targetId: row.id, outcomes: null },
                    {
                      onError: (error) =>
                        showApiError(error, `We could not stop watching ${row.displayName}.`),
                    },
                  ),
              }
            : {
                key: "watch",
                label: "Watch connection",
                icon: <WatchIcon className="h-4 w-4" />,
                disabled: watchUnavailable,
                // A menu item has no tooltip: an account with no address, or a Watches
                // read that failed, says why here. While the read is loading the item is
                // off with no reason on purpose (a press then could reset an "Every
                // result" Watch to the default; the wait is short).
                ...(watchUnavailableReason ? { description: watchUnavailableReason } : {}),
                onClick: () =>
                  watchMutate(
                    {
                      targetType: "dataflow" as const,
                      targetId: row.id,
                      outcomes: WATCH_DEFAULT_OUTCOMES,
                    },
                    {
                      onSuccess: () =>
                        notice.success(
                          `You now watch ${row.displayName}. Failures and warnings, and when it works again, are emailed to you.`,
                        ),
                      onError: (error) =>
                        showApiError(error, `We could not watch ${row.displayName}.`),
                    },
                  ),
              },
          ...(canDelete
            ? [
                { kind: "divider" as const, key: `divider-${row.id}` },
                {
                  key: "delete",
                  label: "Delete connection",
                  icon: <DeleteIcon className="h-4 w-4" />,
                  danger: true,
                  ...(importBusy ? { disabled: true, description: NOT_WHILE_IMPORTING } : {}),
                  onClick: () => onDelete(row),
                },
              ]
            : []),
        ]}
      />
    </div>
  );
}

/** Off, with the reason: no address on the account, or the Watches read failed; null otherwise. */
function watchUnavailableReasonOf(myWatches: ReturnType<typeof useMyWatches>): string | null {
  if (myWatches.data) {
    return myWatches.data.callerEmailMasked === null ? WATCH_NO_EMAIL_MESSAGE : null;
  }
  return myWatches.isError ? WATCH_UNAVAILABLE_MESSAGE : null;
}

/** "Import selected (3)", the cap when past it, or the starts' progress. */
function importSelectedLabel(
  progress: ReturnType<typeof useImportSelected>["progress"],
  selectedCount: number,
): string {
  if (progress) return `Starting ${progress.index} of ${progress.total}…`;
  if (selectedCount > IMPORT_SELECTED_MAX) {
    return `Import selected (${IMPORT_SELECTED_MAX} of ${selectedCount})`;
  }
  return `Import selected (${selectedCount})`;
}

function ListLoadError({ error, onRetry }: { error: ApiClientError; onRetry: () => void }) {
  return (
    <SkyMessageBar
      intent="error"
      layout="multiline"
      testId="connections-error"
      title="We could not load connections"
      message={failureMessage(error, "We could not load connections.")}
      actions={
        <SkyButton size="small" icon={RetryIcon} onClick={onRetry}>
          Try again
        </SkyButton>
      }
    />
  );
}

/** The empty list: what a connection is, and New connection for a writer. */
function NoConnections({ canWrite, onNew }: { canWrite: boolean; onNew: () => void }) {
  return (
    <SkyCard className="max-w-xl p-6" testId="connections-empty">
      <SkyText as="h2" size={400} weight="semibold" block>
        No connections yet
      </SkyText>
      <SkyText as="p" size={300} color="muted" block className="mt-2">
        A connection points skyData at one source system and records which entities to import.
        Create one, validate it, then pick its entities.
      </SkyText>
      {canWrite ? (
        <div className="mt-4">
          <SkyButton
            appearance="primary"
            icon={AddIcon}
            testId="new-connection-empty"
            onClick={onNew}
          >
            New connection
          </SkyButton>
        </div>
      ) : null}
    </SkyCard>
  );
}

function ImportSelectedButton({
  buttonRef,
  label,
  reason,
  reasonId,
  importing,
  onClick,
}: {
  buttonRef: RefObject<HTMLButtonElement | null>;
  label: string;
  /** Why the button does nothing yet; null when it can start. */
  reason: string | null;
  reasonId: string;
  importing: boolean;
  onClick: () => void;
}) {
  return (
    // One click starts; no confirmation (owner item 2). `aria-disabled` rather than
    // `disabled`, with the reason in a tooltip and for screen readers, so focus stays
    // on the button while the starts run (the editor's Import now does the same).
    <span className="inline-flex self-end">
      <SkyTooltip content={reason ?? ""} open={reason ? undefined : false}>
        <SkyButton
          ref={buttonRef}
          appearance="primary"
          icon={PlayIcon}
          testId="connections-import-selected"
          aria-disabled={reason || importing ? true : undefined}
          aria-busy={importing ? true : undefined}
          aria-describedby={reason ? reasonId : undefined}
          className={reason ? "cursor-not-allowed opacity-50" : undefined}
          onClick={onClick}
        >
          {label}
        </SkyButton>
      </SkyTooltip>
      <span id={reasonId} className="sr-only">
        {reason ?? ""}
      </span>
    </span>
  );
}

/** "Delete …?", closed on success and kept open with the reason on a failure (ux-s10 §3). */
function DeleteConnectionDialog({
  target,
  onTargetChange,
  deleteError,
  remove,
  notice,
}: {
  target: DataflowSummary | null;
  onTargetChange: (target: DataflowSummary | null) => void;
  deleteError: ReturnType<typeof useInlineError>;
  remove: ReturnType<typeof useDeleteDataflow>;
  notice: ReturnType<typeof useNotice>;
}) {
  // Round 9 (B-01): the delete takes the connection out of the schedules that name it (the other
  // connections of those schedules keep running), so the dialog names them.
  const schedules = useSchedules(target !== null);
  const named = target
    ? (schedules.data ?? [])
        .filter((schedule) => schedule.dataflowIds.includes(target.id))
        .map((schedule) => schedule.name)
    : [];
  return (
    <SkyDialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) {
          onTargetChange(null);
          deleteError.clear();
        }
      }}
      // The dialog would close itself once the primary action returns, taking a failure with
      // it; it closes on success instead (ux-s10 §3: a dialog that stays open says why inside).
      onBeforeClose={(reason) => reason !== "primaryAction"}
      size="small"
      testId="delete-connection-dialog"
      title={`Delete ${target?.displayName ?? "this connection"}?`}
      description="Deleting a connection removes its entity selection and its stored credentials. Runs already recorded stay in the run history."
      primaryAction={{
        label: "Delete connection",
        destructive: true,
        loading: remove.isPending,
        onClick: () => {
          if (!target) return;
          deleteError.clear();
          remove.mutate(target.id, {
            onSuccess: () => {
              onTargetChange(null);
              // After the close, so the notice is not inside the modal's hidden window.
              notice.success(`${target.displayName} deleted. Its stored credentials are gone.`);
            },
            onError: (error) => deleteError.show(error, "We could not delete that connection."),
          });
        },
      }}
      secondaryAction={{
        label: "Keep connection",
        onClick: () => {
          onTargetChange(null);
          deleteError.clear();
        },
      }}
    >
      <div className="flex flex-col gap-3">
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          message={
            named.length > 0
              ? `It is removed from ${named.length === 1 ? "this schedule" : "these schedules"}, which keep running their other connections: ${named.join(", ")}.`
              : "It is removed from any schedule that names it. Those schedules keep running their other connections."
          }
        />
        <InlineErrorBar
          error={deleteError.error}
          title={deleteError.error?.retryable ? undefined : "We could not delete this connection"}
          testId="delete-connection-error"
        />
      </div>
    </SkyDialog>
  );
}

/**
 * The grid's sort as mirrored for the grid now shown. A mirror kept for another `gridKey` is
 * dropped during render (see the page's note on ruling 15), so it never matches again later.
 */
function useCurrentGridSort(
  gridKey: number,
  sortState: { gridKey: number; sort: GridSort } | null,
  setSortState: (next: null) => void,
): GridSort | null {
  if (sortState && sortState.gridKey !== gridKey) setSortState(null);
  return sortState?.gridKey === gridKey ? sortState.sort : null;
}

/** The loaded list: search and filters, Import selected, the not-started bar, and the grid. */
/**
 * The connections grid. With an Actions column it pins that column at the right edge (B-52), as
 * the Runs grid does, so the row actions stay in view below the 960 px floor (a tablet) instead of
 * waiting at the end of a sideways scroll. A component of its own, remounted by its parent's
 * `key`, because the pinned edge's observer is attached to the grid's region at mount.
 */
function ConnectionsGrid({
  filtered,
  columns,
  selectable,
  selectedIds,
  onSelectionChange,
  onSortChange,
  paginated,
  searchTerm,
}: {
  filtered: ConnectionRow[];
  columns: SkyDataGridColumn<ConnectionRow>[];
  selectable: boolean;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onSortChange: (key: string | null, direction: "asc" | "desc" | null) => void;
  paginated: boolean;
  searchTerm: string;
}) {
  const hasActions = columns.some((column) => column.key === "actions");
  const pinnedEdge = usePinnedColumnEdge<HTMLDivElement>(hasActions);
  const layout = useMemo(
    () =>
      pixelColumnLayout(columns, { minimums: CONNECTIONS_MIN_COLUMN_WIDTH, selection: selectable }),
    [columns, selectable],
  );
  return (
    <div
      ref={pinnedEdge}
      style={{ "--connections-grid-floor": `${layout.floor}px` } as CSSProperties}
    >
      <SkyDataGrid<ConnectionRow>
        // A floor under the percentages: every column keeps room for its header and value;
        // below it the grid scrolls in its region.
        className={hasActions ? CONNECTIONS_GRID_PINNED : CONNECTIONS_GRID_FLOOR}
        testId="connections-grid"
        data={filtered}
        columns={layout.columns}
        getRowId={(row) => row.id}
        selectionMode={selectable ? "multiple" : "none"}
        selectedRows={selectable ? selectedIds : undefined}
        onSelectionChange={(ids) => onSelectionChange(ids.map(String))}
        onSortChange={onSortChange}
        pagination={paginated}
        pageSize={PAGE_SIZE}
        // Naming the term is what tells the user WHICH of the three controls
        // emptied the list — a typo in the box reads very differently from a
        // status filter that happens to match nothing.
        emptyMessage={
          searchTerm
            ? `No connections match ‘${searchTerm}’.`
            : "No connections match those filters."
        }
      />
    </div>
  );
}

function LoadedConnections({
  rows,
  connectorLabel,
  search,
  onSearchChange,
  connectorFilter,
  onConnectorFilterChange,
  statusFilter,
  onStatusFilterChange,
  importButton,
  importResult,
  onDismissImportResult,
  gridKey,
  filtered,
  columns,
  selectable,
  selectedIds,
  onSelectionChange,
  onSortChange,
  searchTerm,
}: {
  rows: DataflowSummary[];
  connectorLabel: ReadonlyMap<string, string>;
  search: string;
  onSearchChange: (search: string) => void;
  connectorFilter: string;
  onConnectorFilterChange: (connector: string) => void;
  statusFilter: ConnectionStatus | typeof ALL;
  onStatusFilterChange: (status: ConnectionStatus | typeof ALL) => void;
  /** Import selected, for a reader who may start runs; null otherwise. */
  importButton: ReactNode;
  importResult: ImportResult;
  onDismissImportResult: () => void;
  /** Remounts the grid when the page count changes (see the page's note). */
  gridKey: number;
  filtered: ConnectionRow[];
  columns: SkyDataGridColumn<ConnectionRow>[];
  selectable: boolean;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onSortChange: (key: string | null, direction: "asc" | "desc" | null) => void;
  searchTerm: string;
}) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {/* A search landmark, so a screen-reader user can jump straight to
          it rather than tabbing the whole filter row. `clearable` is a real
          button labelled "Clear input" by the design system, so Escape is
          not the only way out.
          The label is a real `<label>` rather than an `aria-label`: the two
          dropdowns beside it render one, `SkyInput` drops props it does not
          declare (an `aria-label` here would silently do nothing and leave
          the placeholder as the accessible name), and a visible label is
          the better answer anyway. */}
        <div className="w-72" role="search" aria-label="Search connections">
          <SkyInput
            testId="connections-search"
            label="Search"
            placeholder="Search name, connector or description"
            value={search}
            clearable
            contentBefore={<SearchIcon className="h-4 w-4 text-muted-foreground" />}
            onChange={(event) => onSearchChange(event.target.value)}
            onClear={() => onSearchChange("")}
          />
        </div>
        <div className="w-48">
          <SkyDropdown
            testId="connections-connector-filter"
            label="Source"
            options={[
              { value: ALL, text: "All sources" },
              ...Array.from(new Set(rows.map((row) => row.connectorName))).map((name) => ({
                value: name,
                text: connectorLabel.get(name) ?? name,
              })),
            ]}
            selectedOptions={[connectorFilter]}
            onOptionSelect={(values) => onConnectorFilterChange(values[0] ?? ALL)}
          />
        </div>
        <div className="w-44">
          {/* The test id predates the Status column (it filtered the setup state). */}
          <SkyDropdown
            testId="connections-state-filter"
            label="Status"
            options={[
              { value: ALL, text: "All statuses" },
              ...CONNECTION_STATUSES.map((status) => ({
                value: status,
                text: connectionStatusPresentation(status).label,
              })),
            ]}
            selectedOptions={[statusFilter]}
            onOptionSelect={(values) =>
              onStatusFilterChange(
                CONNECTION_STATUSES.find((status) => status === values[0]) ?? ALL,
              )
            }
          />
        </div>
        {importButton}
        <div className="ml-auto">
          <SkyBadge appearance="ghost" color="subtle">
            <span aria-live="polite" data-testid="connections-count">
              {filtered.length} of {rows.length} connections
            </span>
          </SkyBadge>
        </div>
      </div>

      <ImportSelectedResult
        rows={importResult.notStarted}
        overLimit={importResult.overLimit}
        onDismiss={onDismissImportResult}
      />

      <ConnectionsGrid
        // Remounts the grid when the page count changes (see `gridKey`).
        key={gridKey}
        filtered={filtered}
        columns={columns}
        selectable={selectable}
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
        onSortChange={onSortChange}
        paginated={rows.length > PAGE_SIZE}
        searchTerm={searchTerm}
      />
    </>
  );
}

/**
 * The instance's destination as a column cell: name and logo, or "Not set" (round 10, C2). From
 * the destination summary (#1106), which answers every reader of connections.
 */
function destinationOf(query: { data: DestinationSummaryView | undefined }): {
  label: string;
  iconUrl: string | null;
} {
  const summary = query.data;
  // Nothing is claimed while the summary loads or fails to load.
  if (summary === undefined) return { label: "", iconUrl: null };
  if (!summary.bound || summary.type === null) return { label: "Not set", iconUrl: null };
  return summary.type === "snowflake"
    ? { label: "Snowflake", iconUrl: "/destination-logos/snowflake.svg" }
    : { label: "Databricks", iconUrl: "/destination-logos/databricks.svg" };
}

export default function ConnectionsListPage() {
  const [, navigate] = useLocation();
  const notice = useNotice();
  // A row action's failure: the menu that started it is gone, so it goes to the notice.
  const showApiError = useApiErrorNotice();
  // The delete dialog stays open on a failure and says why inside it (ux-s10 §3).
  const deleteError = useInlineError();
  const { can } = usePermissions();

  const canWrite = can("dataflows.write");
  const canDelete = can("dataflows.delete");
  const canStartRun = can("runs.start");
  const canCancelRun = can("runs.cancel");
  // Session 11 (P1): every role that reads runs gets "View history" (a Viewer included).
  const canReadRuns = can("runs.read");

  const dataflows = useDataflows();
  const connectors = useConnectors();
  // #828 (Level B): each row's last load and Late badge, from skyData's own run records.
  const reliability = useReliability();
  // #2005: a moving row's badge folds the live frames, but nothing re-read the list when no frame
  // came (a dropped socket, a missed NOTIFY), and an idle list held no feed, so a run started by
  // the scheduler never showed. The page holds the feed while open and re-reads the list on a
  // slow timer, every 15 s while a row is queued or running and the socket is not live, every
  // 30 s otherwise; the flip to nothing moving refreshes Last loaded with it.
  useLiveRunRefresh({
    active: (dataflows.data ?? []).some(
      (row) =>
        Boolean(row.activeRunId) || (row.lastRun ? !isTerminalRun(row.lastRun.status) : false),
    ),
    poll: () => void dataflows.refetch(),
  });
  const destination = destinationOf(useDestinationSummary());
  // Round 6, S5: the row whose "Import with options" opened Run now; null while closed.
  const [optionsTarget, setOptionsTarget] = useState<DataflowSummary | null>(null);
  // 2026-09-28 cred-check: a small "Credentials" badge on the rows whose check needs attention.
  const credentials = useCredentials();
  const remove = useDeleteDataflow();

  const [search, setSearch] = useState("");
  const [connectorFilter, setConnectorFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState<ConnectionStatus | typeof ALL>(ALL);
  const [pendingDelete, setPendingDelete] = useState<DataflowSummary | null>(null);
  const [cancelTarget, setCancelTarget] = useState<CancelImportTarget | null>(null);

  // Session 10 (P12): the list starts an import through `useStartRun` (one start per connection in
  // flight, a new Idempotency-Key per settled press, and the invalidation the local mutation never did), and the row shows it at once.
  const { startImport, pendingDataflowId } = useStartImport();
  const started = useStartedRuns();
  // 2026-09-28: Disable and Enable from the row menu; the mutation reloads the list itself.
  const toggle = useConnectionToggle();
  const openToggle = toggle.open;

  // 2026-09-28 (alert routing): the caller's own Watches. Anyone who reads connections may
  // watch one; the row menu offers it beside Disable and Enable, and a watched row says so.
  const myWatches = useMyWatches();
  const setWatch = useSetWatch();
  const watchMutate = setWatch.mutate;
  const watchedIds = useMemo(
    () =>
      new Set(
        (myWatches.data?.watches ?? [])
          .filter((watch) => watch.targetType === "dataflow")
          .map((watch) => watch.targetId),
      ),
    [myWatches.data],
  );
  // Off, with the reason, for an account with no address or a Watches read that failed. Off with
  // no words for the moment the read is loading: until it answers a watched row cannot say "Stop
  // watching", and a press would reset its "Every result" to the default.
  const watchUnavailableReason = watchUnavailableReasonOf(myWatches);
  const watchUnavailable = !myWatches.data || watchUnavailableReason !== null;

  // Session 11 (P1, ruling D2): Import selected. The page owns the selection (the grid is
  // controlled), the grid's sort (mirrored for the start order) and the not-started bar.
  // Each is stored with the view it belongs to, so a change of view drops it without an effect.
  const [selection, setSelection] = useState<{ view: string; ids: string[] }>({
    view: "",
    ids: [],
  });
  const [sortState, setSortState] = useState<{ gridKey: number; sort: GridSort } | null>(null);
  const [importResult, setImportResult] = useState<ImportResult>(NO_IMPORT_RESULT);
  const { importSelected, progress } = useImportSelected();
  const importReasonId = useId();
  // #1080 review: Import selected starts the same imports as each row's Import now, so it is off
  // for the destination's reason too (the server answers 409 DESTINATION_NOT_SET or
  // DESTINATION_NOT_READY to each start otherwise).
  const destinationReason = destinationBlockReason(useDestinationReadiness());
  // Dismissing the bar returns focus to the control that produced it.
  const importButtonRef = useRef<HTMLButtonElement>(null);

  const rows = useMemo(() => dataflows.data ?? [], [dataflows.data]);
  // #1650: a refused Import now clears once its row is live or has moved on to another run.
  const refusalEntries = useMemo(
    () =>
      rows.map((row) => ({
        id: row.id,
        runId: row.lastRun?.runId ?? null,
        live: isImporting(row, pendingDataflowId, started),
      })),
    [rows, pendingDataflowId, started],
  );
  const { remember: rememberRefusal } = useImportStartRefusals(refusalEntries);

  /**
   * Each row's status for the filter and the sort: the list's own read, plus an import this tab
   * started that the list has not caught up with yet (it reads Running at once, as its cell does).
   */
  const statusRows = useMemo<ConnectionRow[]>(
    () =>
      rows.map((row) => {
        const status = connectionStatusOf(row, startedAheadOfList(row, started) ? "queued" : null);
        return { ...row, status: connectionStatusPresentation(status).label };
      }),
    [rows, started],
  );

  const connectorLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const connector of connectors.data ?? []) map.set(connector.name, connector.displayName);
    return map;
  }, [connectors.data]);

  const connectorIcon = useMemo(() => {
    const map = new Map<string, string>();
    for (const connector of connectors.data ?? []) {
      if (connector.iconUrl) map.set(connector.name, connector.iconUrl);
    }
    return map;
  }, [connectors.data]);

  /** The settled term as the user typed it, for echoing back; and folded, for matching. */
  const searchTerm = useDebouncedValue(search, SEARCH_DEBOUNCE_MS).trim();
  const needle = searchTerm.toLowerCase();

  /**
   * What the search matches on, per row.
   *
   * The connector's DISPLAY name is in here and it is the reason this is a
   * separate memo: a row carries `connectorName`, the slug (`sqlserver`), while
   * the screen shows "SQL Server", and searching for what is on the screen has
   * to work. The slug stays in the haystack too — it is what an engineer reading
   * the API or a seed file has in their head.
   *
   * The source host is NOT here, and not because it was forgotten: `/api/v2/dataflows`
   * returns `DataflowSummary`, which has no host — the host lives in a field row
   * (`serverName` for SQL Server, a different id per connector) that only
   * `DataflowDetail` carries. Searching it means either a field on the summary or
   * 50 detail fetches, and that is a server change, not a client one.
   */
  const haystacks = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of rows) {
      map.set(
        row.id,
        [
          row.name,
          row.displayName,
          row.description,
          row.connectorName,
          connectorLabel.get(row.connectorName),
        ]
          .filter((value): value is string => Boolean(value))
          .join(" ")
          .toLowerCase(),
      );
    }
    return map;
  }, [rows, connectorLabel]);

  const statusFilterLabel =
    statusFilter === ALL ? null : connectionStatusPresentation(statusFilter).label;
  const filtered = useMemo(
    () =>
      statusRows
        .filter((row) => (needle ? (haystacks.get(row.id) ?? "").includes(needle) : true))
        .filter((row) => (connectorFilter === ALL ? true : row.connectorName === connectorFilter))
        .filter((row) => (statusFilterLabel === null ? true : row.status === statusFilterLabel)),
    [statusRows, needle, haystacks, connectorFilter, statusFilterLabel],
  );

  /**
   * A remount key for the grid, and a scar worth spelling out.
   *
   * `SkyDataGrid` keeps `currentPage` in its own state and has no effect that
   * clamps it when the `data` prop shrinks — it slices `(page - 1) * pageSize`
   * out of whatever it is handed. So a user on page 2 who then searches down to
   * three matches gets three rows sliced away to nothing: a grid that renders
   * empty while the count badge beside it says "3 of 60". Changing this key
   * remounts the grid, which resets it to page 1.
   *
   * The key is the PAGE COUNT rather than the search term on purpose. Remounting
   * also clears the user's column sort, and the only state that can strand the
   * grid is a page number that no longer exists — which is exactly when the page
   * count falls. Narrowing a search inside the same page count therefore keeps
   * the sort, and below the pagination threshold the count is always 1 and the
   * grid never remounts at all.
   */
  const gridKey = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  // A remount resets the grid's own sort, so the mirror is only good for the grid it came from.
  // It is dropped as soon as the key moves, not merely hidden: the grid never calls
  // `onSortChange` on a remount, so a mirror kept for key 2 would match again after the page
  // count went 2 -> 1 -> 2 while the remounted grid shows no sort (ruling 15).
  const gridSort = useCurrentGridSort(gridKey, sortState, setSortState);

  // The selection is cleared when the search or a filter changes which rows are visible.
  const selectionView = JSON.stringify([needle, connectorFilter, statusFilter]);
  const selectedIds = selection.view === selectionView ? selection.ids : NO_SELECTION;
  const setSelectedIds = (ids: string[]) => setSelection({ view: selectionView, ids });

  /** The selected rows that are on the list, in the grid's order. */
  const selectedRows = useMemo(() => {
    const chosen = new Set(selectedIds);
    return inGridOrder(
      filtered.filter((row) => chosen.has(row.id)),
      gridSort,
    );
  }, [filtered, selectedIds, gridSort]);
  const selectedCount = selectedRows.length;
  const importing = progress !== null;

  const onImportSelected = () => {
    if (importing || selectedCount === 0 || destinationReason !== null) return;
    setImportResult(NO_IMPORT_RESULT);
    void importSelected(selectedRows, (row) => isImporting(row, pendingDataflowId, started)).then(
      (outcome) => {
        setImportResult({ notStarted: outcome.notStarted, overLimit: outcome.overLimit.length });
        // The rows past the cap stay selected, so the next click starts them.
        setSelectedIds(outcome.overLimit.map((row) => row.dataflowId));
        const count = outcome.started.length;
        const only = outcome.started[0];
        if (count === 1 && only) {
          notice.success(`Import started for ${only.name}.`, {
            action: {
              label: "View import",
              onClick: () => navigate(absolutePath(`/runs/${only.runId}`)),
            },
          });
        } else if (count > 1) {
          notice.success(`Import started for ${count} connections.`, {
            action: {
              label: "View imports",
              onClick: () => navigate(absolutePath("/runs?kind=on_demand&type=import")),
            },
          });
        }
      },
    );
  };

  const importLabel = importSelectedLabel(progress, selectedCount);
  const importReason =
    selectedCount === 0 ? "Select the connections to import." : destinationReason;

  const columns = useMemo<SkyDataGridColumn<ConnectionRow>[]>(() => {
    const list: SkyDataGridColumn<ConnectionRow>[] = [
      {
        // The grid sorts by `row[key]`: the Display name a person reads (instance 0014), not the
        // table-name identifier `name`.
        key: "displayName",
        header: "Name",
        width: "13%",
        sortable: true,
        renderCell: (_value, row) => (
          <span className="flex flex-wrap items-center gap-2">
            {/* Reading a connection is allowed for everyone who reached this
                screen, so the name stays a link for a Viewer too. */}
            <Link
              href={absolutePath(connectionPath(row.id))}
              className="text-sm font-medium text-accent-text underline-offset-2 hover:underline"
              data-testid={`connection-link-${row.displayName}`}
            >
              {row.displayName}
            </Link>
            {/* Round 10 (C4): the caller's own Watch, its level in words, changed in place.
                Its clicks never tick the row (`stopRowSelection` inside the badge). */}
            <WatchBadge
              targetType="dataflow"
              targetId={row.id}
              name={row.displayName}
              subject={row.displayName}
              compact
              testIds={{
                watch: `watch-${row.displayName}`,
                watching: `watching-${row.displayName}`,
              }}
            />
          </span>
        ),
      },
      {
        key: "connectorName",
        header: "Source",
        // The logo, its gap and the widest word of a connector's name, "PointClickCare" (161 px).
        width: "161px",
        sortable: true,
        renderCell: (_value, row) => {
          const label = connectorLabel.get(row.connectorName) ?? row.connectorName;
          return (
            <span className="flex items-center gap-2">
              {/* Session 11 (P1): 28 px, or the connector's first letter in a muted square. */}
              <ConnectorLogo iconUrl={connectorIcon.get(row.connectorName)} name={label} />
              <SkyText color="muted">{label}</SkyText>
            </span>
          );
        },
      },
      {
        // Round 10 (C2): where this instance loads its data, from the binding the Settings page
        // reads. One destination per instance, so every row says the same; "Not set" until the
        // Destination tab is saved. The connection's own type is not shown.
        key: "id",
        header: "Destination",
        // The header on one line (137 px), and the logo with "Databricks" (136).
        width: "137px",
        renderCell: () => (
          <span className="flex items-center gap-2" data-testid="connection-destination">
            {destination.iconUrl ? (
              <ConnectorLogo iconUrl={destination.iconUrl} name={destination.label} />
            ) : null}
            <SkyText color="muted">{destination.label}</SkyText>
          </span>
        ),
      },
      {
        // The connection's one status (owner feedback, 2026-09-28); a moving import adds its
        // step as a second line. Sorted by the word on screen.
        key: "status",
        header: "Status",
        // The widest badge, "Setup incomplete" (144 px); the live step line wraps under it.
        width: "144px",
        sortable: true,
        renderCell: (_value, row) => (
          <span className="flex flex-col items-start gap-1">
            <ConnectionStatusCell dataflow={row} />
            <CredentialListBadge target={credentialTarget(credentials.data, "dataflow", row.id)} />
            {/* #828: overdue by its schedules (the freshness rule skyMonitor uses). */}
            <LateBadge
              connection={connectionReliabilityOf(reliability.data, row.id)}
              testId={`connection-late-${row.displayName}`}
            />
          </span>
        ),
      },
      {
        key: "selectedEntityCount",
        header: "Tables",
        width: "10%",
        align: "right",
        sortable: true,
        renderCell: (_value, row) => (
          <SkyText font="numeric">
            {row.selectedEntityCount > 0 ? (
              row.selectedEntityCount
            ) : (
              // #971: an empty value is a dash like the others, and the tooltip says what it means.
              <SkyTooltip content="No tables selected">
                <span tabIndex={0} aria-label="No tables selected">
                  —
                </span>
              </SkyTooltip>
            )}
          </SkyText>
        ),
      },
      {
        key: "lastRefreshAt",
        header: "Last import",
        width: "17%",
        renderCell: (_value, row) => (
          <span className="flex flex-col items-start gap-1">
            <LastRunCell dataflow={row} />
            {/* #828: when an import last landed a table, which a failed last import does not move. */}
            <LastLoadedLine
              connection={connectionReliabilityOf(reliability.data, row.id)}
              testId={`connection-last-loaded-${row.displayName}`}
            />
          </span>
        ),
      },
      // Session 10 (B17): this connection's rows in its last run, and that run's whole time.
      ...connectionRunColumns(),
    ];

    if (canWrite || canDelete || canStartRun || canCancelRun || canReadRuns) {
      list.push({
        key: "actions",
        header: "Actions",
        // The header (99 px with its padding); the two 28 px buttons and their gap need 84.
        width: "99px",
        align: "right",
        renderCell: (_value, row) => (
          <ConnectionRowActions
            row={row}
            canReadRuns={canReadRuns}
            canStartRun={canStartRun}
            canCancelRun={canCancelRun}
            canWrite={canWrite}
            canDelete={canDelete}
            importBusy={isImporting(row, pendingDataflowId, started)}
            cancelRunId={cancellableRunId(row, started)}
            watched={watchedIds.has(row.id)}
            watchUnavailable={watchUnavailable}
            watchUnavailableReason={watchUnavailableReason}
            navigate={navigate}
            startImport={startImport}
            rememberRefusal={rememberRefusal}
            notice={notice}
            showApiError={showApiError}
            openToggle={openToggle}
            watchMutate={watchMutate}
            onImportWithOptions={setOptionsTarget}
            onDelete={setPendingDelete}
            onCancelImport={(target, runId) =>
              setCancelTarget({ runId, dataflowId: target.id, name: target.displayName })
            }
          />
        ),
      });
    }

    return list;
  }, [
    credentials.data,
    reliability.data,
    canWrite,
    canDelete,
    canStartRun,
    canCancelRun,
    canReadRuns,
    connectorLabel,
    connectorIcon,
    destination,
    navigate,
    startImport,
    rememberRefusal,
    pendingDataflowId,
    started,
    notice,
    showApiError,
    openToggle,
    watchedIds,
    watchUnavailable,
    watchUnavailableReason,
    watchMutate,
  ]);

  const error = dataflows.error as ApiClientError | null;
  const importButton = canStartRun ? (
    <ImportSelectedButton
      buttonRef={importButtonRef}
      label={importLabel}
      reason={importReason}
      reasonId={importReasonId}
      importing={importing}
      onClick={onImportSelected}
    />
  ) : null;

  function renderConnections(): ReactNode {
    if (dataflows.isLoading) {
      return <LoadingRows />;
    }
    if (rows.length === 0 && !error) {
      return (
        <NoConnections
          canWrite={canWrite}
          onNew={() => navigate(absolutePath(NEW_CONNECTION_PATH))}
        />
      );
    }
    return (
      <LoadedConnections
        rows={rows}
        connectorLabel={connectorLabel}
        search={search}
        onSearchChange={setSearch}
        connectorFilter={connectorFilter}
        onConnectorFilterChange={setConnectorFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        importButton={importButton}
        importResult={importResult}
        onDismissImportResult={() => {
          setImportResult(NO_IMPORT_RESULT);
          importButtonRef.current?.focus();
        }}
        gridKey={gridKey}
        filtered={filtered}
        columns={columns}
        selectable={canStartRun}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onSortChange={(key, direction) =>
          setSortState(key && direction ? { gridKey, sort: { key, direction } } : null)
        }
        searchTerm={searchTerm}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="connections-page">
      {/* The list's one polite live line (SF13; P12 lead fix round, S1): outside the grid, in
          the page before any start, speaking only for the row started from this tab. */}
      {canStartRun ? <ConnectionsImportLiveLine dataflows={rows} /> : null}
      <TopbarActionsPortal>
        <SkyButton
          appearance="secondary"
          icon={RetryIcon}
          testId="connections-refresh"
          loading={dataflows.isFetching}
          onClick={() => void dataflows.refetch()}
        >
          Refresh
        </SkyButton>
        {canWrite ? (
          <SkyButton
            appearance="primary"
            icon={AddIcon}
            testId="new-connection"
            onClick={() => navigate(absolutePath(NEW_CONNECTION_PATH))}
          >
            New connection
          </SkyButton>
        ) : null}
      </TopbarActionsPortal>

      {error ? <ListLoadError error={error} onRetry={() => void dataflows.refetch()} /> : null}

      {renderConnections()}

      {toggle.dialog}

      {/* Mounted only while open, so the row it was opened for is the one it imports. */}
      {canStartRun && optionsTarget ? (
        <RunNowDialog
          open
          onOpenChange={(open) => {
            if (!open) setOptionsTarget(null);
          }}
          connections={[optionsTarget]}
          schedules={[]}
          initialTarget={{ kind: "dataflow", id: optionsTarget.id }}
          allowKindChoice={false}
          targetFixed
        />
      ) : null}

      <CancelImportDialog target={cancelTarget} onClose={() => setCancelTarget(null)} />

      <DeleteConnectionDialog
        target={pendingDelete}
        onTargetChange={setPendingDelete}
        deleteError={deleteError}
        remove={remove}
        notice={notice}
      />
    </div>
  );
}
